import React, { useState } from "react";
import api, { money } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton } from "@/components/lux";
import { Search, Plus } from "lucide-react";

const AVATAR = "/avatar.png";
const ringFor = (node, isYou) => (isYou ? "#22c55e" : node?.activation_type === "PINK_PIN" ? "#ec4899" : node?.status === "active" ? "#d13b3b" : "#9aa3b2");

function LRBar({ small }) {
  const box = small ? "h-5 w-5 text-[10px]" : "h-6 w-7 text-xs";
  const w = small ? "w-24" : "w-44";
  return (
    <div className={`relative mx-auto mt-2 mb-1 ${w}`}>
      <div className="absolute left-0 right-0 top-1/2 h-px bg-[#c3ccdb]" />
      <div className="absolute left-1/2 top-0 h-1/2 w-px -translate-x-1/2 bg-[#c3ccdb]" />
      <div className="relative flex items-center justify-between">
        <span className={`grid ${box} place-items-center rounded border border-[rgba(245,197,66,.28)] bg-[rgba(255,255,255,.05)] font-bold text-[var(--text-main)]`}>L</span>
        <span className={`grid ${box} place-items-center rounded border border-[rgba(245,197,66,.28)] bg-[rgba(255,255,255,.05)] font-bold text-[var(--text-main)]`}>R</span>
      </div>
    </div>
  );
}

function AddNew({ sponsor, side }) {
  return (
    <a href={`/register?sponsor=${sponsor}&side=${side}`} className="flex flex-col items-center gap-1" data-testid={`add-new-${sponsor}-${side}`}>
      <span className="grid h-16 w-16 place-items-center overflow-hidden rounded-full border-[3px] border-[rgba(255,255,255,.14)] grayscale">
        <img src={AVATAR} alt="add" className="h-full w-full object-cover opacity-60" />
      </span>
      <span className="text-xs font-semibold text-[var(--gold-2)]">Add New</span>
    </a>
  );
}

function Node({ node, isYou, onInfo }) {
  const ring = ringFor(node, isYou);
  return (
    <button onClick={() => onInfo(node)} className="flex flex-col items-center gap-1" data-testid={`tree-node-${node.member_id}`}>
      <span className="grid h-16 w-16 place-items-center overflow-hidden rounded-full shadow" style={{ border: `3px solid ${ring}` }}>
        <img src={AVATAR} alt={node.member_id} className="h-full w-full object-cover" style={node.status === "active" ? {} : { filter: "grayscale(1)" }} />
      </span>
      <span className="text-xs font-bold text-[var(--text-main)]">{node.member_id}</span>
      {node.name ? <span className="max-w-[110px] truncate text-[11px] text-[var(--text-dim)]">{node.name}</span> : null}
      {node.activation_type === "PINK_PIN" ? <span className="rounded-full bg-[rgba(236,72,153,.18)] border border-[rgba(236,72,153,.45)] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white" data-testid={`tree-pinkpin-${node.member_id}`}>Pink Pin</span> : null}
    </button>
  );
}

function Slot({ node, sponsor, side, isYou, onInfo }) {
  return (
    <div className="flex flex-col items-center px-2">
      {node ? <Node node={node} isYou={isYou} onInfo={onInfo} /> : <AddNew sponsor={sponsor} side={side} />}
    </div>
  );
}

function Subtree({ node, onInfo }) {
  // renders a node + its L/R children + grandchildren (Add New where empty)
  return (
    <div className="flex flex-col items-center px-3">
      <Node node={node} onInfo={onInfo} />
      <LRBar small />
      <div className="flex items-start justify-center gap-3">
        {[["left", node.left], ["right", node.right]].map(([side, child]) => (
          <div key={side} className="flex flex-col items-center">
            {child ? (
              <>
                <Node node={child} onInfo={onInfo} />
                <LRBar small />
                <div className="flex items-start justify-center gap-2">
                  <Slot node={child.left} sponsor={child.member_id} side="left" onInfo={onInfo} />
                  <Slot node={child.right} sponsor={child.member_id} side="right" onInfo={onInfo} />
                </div>
              </>
            ) : <AddNew sponsor={node.member_id} side={side} />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Binary() {
  const { data, loading, reload } = useFetch("/user/binary");
  const { data: team } = useFetch("/user/team");
  const [tree, setTree] = useState(null);
  const [search, setSearch] = useState("");
  const [info, setInfo] = useState(null);
  const focus = async (mid) => {
    try { const r = await api.get(`/user/binary?root_member=${mid}`); setTree(r.data); window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* ignore */ }
  };
  const root = tree || data;
  const c = team?.counts || {};
  return (
    <div className="space-y-4">
      <PageTitle title="Genealogy Tree" subtitle="Tap any member for details. Add New opens a referral signup on that leg." right={
        <div className="flex gap-2">
          <div className="flex items-center gap-2 rounded-xl border px-3" style={{ borderColor: "var(--line)", background: "#fff" }}>
            <Search size={16} className="text-[var(--text-dim)]" />
            <input className="bg-transparent py-2 text-sm text-[var(--text-main)] outline-none" placeholder="Search By User ID" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="tree-search" />
          </div>
          <LuxuryButton size="sm" onClick={() => focus(search)} data-testid="tree-jump">Search</LuxuryButton>
          <LuxuryButton variant="dark" size="sm" onClick={() => { setTree(null); reload(); }}>Reset</LuxuryButton>
        </div>
      } />

      <GlassCard className="overflow-x-auto p-6" style={{ background: "#ffffff", border: "1px solid #e6eaf0" }}>
        <div className="min-w-max">
          <div className="mb-2 flex items-start justify-between gap-6 font-bold text-[var(--text-main)]" data-testid="tree-totals">
            <div className="leading-tight">
              <p>Left Total Team : <span>{c.left ?? "—"}</span></p>
              <p>Left Business : <span>${money(root?.left_real || 0, 0)}</span></p>
            </div>
            <div className="text-right leading-tight">
              <p>Right Total Team : <span>{c.right ?? "—"}</span></p>
              <p>Right Business : <span>${money(root?.right_real || 0, 0)}</span></p>
            </div>
          </div>
          {loading ? <div className="skeleton h-40" /> : root ? (
            <div className="flex flex-col items-center pt-2">
              <Node node={root} isYou={!tree} onInfo={setInfo} />
              <LRBar />
              <div className="flex items-start justify-center gap-10">
                {[["left", root.left], ["right", root.right]].map(([side, child]) => (
                  <div key={side}>{child ? <Subtree node={child} onInfo={setInfo} /> : <AddNew sponsor={root.member_id} side={side} />}</div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </GlassCard>

      {info && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={() => setInfo(null)} data-testid="tree-info-modal">
          <div className="w-full max-w-sm rounded-2xl bg-[rgba(255,255,255,.05)] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <img src={AVATAR} alt="" className="h-14 w-14 rounded-full" style={{ border: `3px solid ${ringFor(info)}` }} />
              <div><p className="text-lg font-bold text-[var(--text-main)]">{info.name || info.member_id}</p><p className="text-xs text-[var(--text-dim)]">ID {info.member_id}</p></div>
            </div>
            <div className="mt-3 space-y-1 text-sm text-[var(--text-main)]">
              <p><span className="text-[var(--text-dim)]">Rank:</span> <b>{info.rank || "Associate"}</b></p>
              <p><span className="text-[var(--text-dim)]">Package:</span> <b>${money(info.package, 0)}</b></p>
              <p><span className="text-[var(--text-dim)]">Status:</span> <b style={{ color: info.status === "active" ? "#127a3c" : "#a15c00" }}>{info.status}</b></p>
              {info.activation_type === "PINK_PIN" ? <p><span className="text-[var(--text-dim)]">Type:</span> <b style={{ color: "#ec4899" }}>PINK PIN · Free Activation</b></p> : null}
              <p><span className="text-[var(--text-dim)]">Left/Right Business:</span> <b>${money(info.left_real, 0)} / ${money(info.right_real, 0)}</b></p>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => { const m = info.member_id; setInfo(null); focus(m); }} className="flex-1 rounded-lg py-2 text-sm font-bold text-white" style={{ background: "#123C8C" }} data-testid="tree-drill">View Subtree</button>
              <button onClick={() => setInfo(null)} className="rounded-lg border px-4 py-2 text-sm font-semibold text-[var(--text-main)]" style={{ borderColor: "var(--line)", background: "rgba(255,255,255,.04)" }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
